import React, { Suspense, useEffect, useRef, useState } from 'react';

/**
 * DEV-12330: Defer mounting a below-the-fold section until it is either near the
 * viewport (scroll) or the main thread goes idle — whichever comes first.
 *
 * Why: the marketing homepage builds its entire section tree in ONE synchronous
 * React commit on load, which is a single long task that dominates Total Blocking
 * Time on mobile. Wrapping the below-fold sections lets the initial commit render
 * only the nav + hero; each deferred section then mounts in its own later commit
 * (a separate, shorter task), so no single task blocks the main thread.
 *
 * SEO / no-scroll safety: the idle fallback (requestIdleCallback, with a timeout)
 * guarantees the content is in the DOM shortly after load even if the user never
 * scrolls and even for crawlers that execute JS without scrolling — it is simply
 * mounted OUTSIDE the initial blocking window instead of inside it.
 *
 * CLS safety: `minHeight` reserves the section's approximate height while it is a
 * placeholder, so when it upgrades the document height barely changes and the
 * swap happens below the fold before the user reaches it.
 */
interface DeferredSectionProps {
  /** Approximate rendered height (px) to reserve so the upgrade doesn't shift layout. */
  minHeight: number;
  children: React.ReactNode;
}

export const DeferredSection: React.FC<DeferredSectionProps> = ({ minHeight, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    let observer: IntersectionObserver | undefined;
    let idleId: number | undefined;

    const reveal = () => setShow(true);

    // Mount just before the section scrolls into view.
    if (el && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) reveal();
        },
        // DEV-12444: 600px pulled the first below-fold section into the initial
        // desktop viewport (900px + 600px > hero height) and mounted it during
        // the 3.5s settle window. 100px still prefetches just before scroll
        // without capturing the first section on load.
        { rootMargin: '100px' },
      );
      observer.observe(el);
    } else {
      reveal();
    }

    // ...or after a fixed delay, so content still exists for no-scroll views and
    // crawlers. The delay is deliberately PAST the initial load/blocking window
    // (~3.5s under mobile throttle): mounting these below-fold sections earlier
    // (e.g. via an eager requestIdleCallback, which fires the moment the thread is
    // idle) would just move their React commits back inside the Total-Blocking-Time
    // window. Real users who scroll get them immediately via the observer above.
    idleId = window.setTimeout(reveal, 4000);

    return () => {
      observer?.disconnect();
      if (idleId !== undefined) window.clearTimeout(idleId);
    };
  }, [show]);

  // DEV-12641: Suspense lives inside the reserved box. Wrapping this
  // component in `<Suspense fallback={null}>` unmounts the placeholder when
  // a lazy child first suspends, drops `minHeight`, and remounts `show=false`.
  return (
    <div ref={ref} style={show ? undefined : { minHeight }}>
      {show ? (
        <Suspense fallback={<div style={{ minHeight }} aria-hidden="true" />}>
          {children}
        </Suspense>
      ) : null}
    </div>
  );
};

export default DeferredSection;
